export type IScopeRule = (availableScopes: string[], usingOrgToken:boolean) => boolean
export type ScopeRequirement = string | IScopeRule

export interface IScopeRules {
  "AND": (requirements: ScopeRequirement[]) => IScopeRule
  "OR": (requirements: ScopeRequirement[]) => IScopeRule
  "NOT": (requirement: ScopeRequirement) => IScopeRule

  //ORG action makes sure that the token is a valid org token 
  "ORG": (requirement: ScopeRequirement) => IScopeRule
}

/**
 * Test if available scopes satisfy a scope requirement
 */
export function testScopes(availableScopes: string[], requirement: ScopeRequirement, usingOrgToken:boolean = false): boolean {
  if (typeof requirement === 'string') {
    return availableScopes.includes(requirement)
  }
  
  // IScopeRule function
  return requirement(availableScopes, usingOrgToken)
}

export const ScopeRule: IScopeRules = { 
  "AND": (requirements: ScopeRequirement[]) => {
    return (availableScopes: string[], usingOrgToken:boolean) => 
      requirements.every(req => testScopes(availableScopes, req, usingOrgToken))
  },
  "OR": (requirements: ScopeRequirement[]) => {
    return (availableScopes: string[], usingOrgToken:boolean) => 
      requirements.some(req => testScopes(availableScopes, req, usingOrgToken))
  },
  "NOT": (requirement: ScopeRequirement) => {
    return (availableScopes: string[], usingOrgToken:boolean) => 
      !testScopes(availableScopes, requirement, usingOrgToken)
  },
  "ORG": (requirement: ScopeRequirement) => {
    return (availableScopes: string[], usingOrgToken:boolean) => {
      // First check if we have an organization context
      if (!usingOrgToken) {
        return false // Organization-scoped scopes require org context
      }
      // Then check the inner requirement
      return testScopes(availableScopes, requirement, usingOrgToken)
    }
  }
}

export const scopeToString = (scope: ScopeRequirement) => {
  if (typeof scope === 'string') {
    return scope
  }
  return JSON.stringify(scope)
}

/*
Usage examples:

// Require a scope that only works in organization context
const orgOnlyScope = ScopeRule.ORG("read:api-keys")

// Require organization context AND another scope
const orgWithScope = ScopeRule.AND([
  ScopeRule.ORG("read:api-keys"),
  "read:organizations"
])

// Require organization context OR a global scope
const orgOrGlobal = ScopeRule.OR([
  ScopeRule.ORG("read:api-keys"),
  "admin:all"
])

// Require organization context but NOT a specific scope
const orgButNotScope = ScopeRule.AND([
  ScopeRule.ORG("read:api-keys"),
  ScopeRule.NOT("admin:all")
])
*/ 
